import React, { useState } from "react";
import axios from "axios";
import "../styles/FeePayment.css";
import API_URL from "../apiConfig";

function FeePayment({ user, token }) {
  const [amount, setAmount] = useState("");
  const [onlineMethod, setOnlineMethod] = useState("Netbanking");
  const [reference, setReference] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [showBillDesk, setShowBillDesk] = useState(false);
  const [isRefund, setIsRefund] = useState(false);
  const [year, setYear] = useState(""); // selected year/class

  const onlineMethods = [
    { value: "Netbanking", label: "Netbanking (BillDesk)" },
    { value: "UPI", label: "UPI" },
    { value: "CreditCard", label: "Credit Card" },
    { value: "DebitCard", label: "Debit Card" },
    { value: "Wallet", label: "Wallet (Paytm/PhonePe/Google Pay)" },
  ];

  // Defensive fallback for session if not provided in user object
  const sessionYear = user?.session_year || user?.session || "";

  const handlePayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setTransactionId("");

    const studentId =
      typeof user.student_id !== "undefined"
        ? user.student_id
        : user.id;

    if (
      typeof studentId === "undefined" ||
      studentId === null ||
      isNaN(Number(studentId))
    ) {
      setMessage("❌ Invalid student ID. Cannot proceed with payment.");
      setLoading(false);
      return;
    }

    if (!year) {
      setMessage("❌ Please select the Year/Class for which you are paying.");
      setLoading(false);
      return;
    }

    const payload = {
      amount: Math.abs(Number(amount)),
      orderId: "ORD" + Date.now(),
      customerId: Number(studentId),
      method: onlineMethod,
      reference,
      isRefund,
      course_year: Number(year), // 1,2,3
      session_year: sessionYear
    };

    try {
      if (isRefund) {
        // Refund (scholarship/fee return)
        await axios.post(
          `${API_URL}/api/fees/add`,
          {
            student_id: studentId,
            amount: 0,
            amount_paid: 0,
            scholarship_amount: payload.amount,
            status: "refunded",
            payment_date: new Date().toISOString().slice(0, 10),
            mode: onlineMethod,
            reference,
            session_year: sessionYear,
            course_year: payload.course_year,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage("✅ Refund processed successfully!");
        setAmount("");
        setReference("");
        setYear("");
        setTransactionId(payload.orderId);
        setIsRefund(false);
      } else {
        // Fee payment
        if (user.role === "admin") {
          await axios.post(
            `${API_URL}/api/student/pay`,
            {
              studentId,
              amount: payload.amount,
              mode: "Online",
              method: onlineMethod,
              status: "pending",
              reference,
              session_year: sessionYear,
              course_year: payload.course_year,
            },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setMessage("✅ Payment submitted successfully! Await confirmation.");
          setAmount("");
          setOnlineMethod("Netbanking");
          setReference("");
          setYear("");
        } else {
          // Student: simulate BillDesk payment
          const res = await axios.post(
            `${API_URL}/api/payment/initiate`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.success) {
            setShowBillDesk(true);
            setTimeout(() => {
              setShowBillDesk(false);
              setTransactionId(payload.orderId);
              setMessage("✅ Payment Successful! Transaction ID: " + payload.orderId);
              setAmount("");
              setOnlineMethod("Netbanking");
              setYear("");
            }, 2000);
          } else {
            setMessage(
              "❌ Payment failed: " + (res.data.message || "Unknown error")
            );
          }
        }
      }
    } catch (err) {
      setMessage(
        err.response?.data?.message || "❌ Payment failed. Please try again."
      );
    }
    setLoading(false);
  };

  return (
    <div className="feepayment-bg">
      <div className="feepayment-card">
        <h2>{isRefund ? "Process Fee Refund" : "Pay Your Fees"}</h2>
        <form className="feepayment-form" onSubmit={handlePayment}>
          <div className="feepayment-field">
            <label>Fee Year/Class</label>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              required
            >
              <option value="">Select Year</option>
              <option value={1}>1st Year GNM</option>
              <option value={2}>2nd Year GNM</option>
              <option value={3}>3rd Year GNM</option>
            </select>
          </div>
          <div className="feepayment-field">
            <label>{isRefund ? "Refund Amount" : "Amount"}</label>
            <input
              type="number"
              min="1"
              required
              value={amount}
              placeholder={isRefund ? "Enter refund amount" : "Enter fee amount"}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="feepayment-field">
            <label>Payment Mode</label>
            <input
              type="text"
              value="Online"
              readOnly
              className="feepayment-readonly"
            />
          </div>
          <div className="feepayment-field">
            <label>Online Method</label>
            <select
              value={onlineMethod}
              onChange={(e) => setOnlineMethod(e.target.value)}
            >
              {onlineMethods.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {(user.role === "admin" || isRefund) && (
            <div className="feepayment-field">
              <label>Reference/Transaction ID</label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Bank Transaction/UPI/Ref No."
              />
            </div>
          )}
          {user.role === "admin" && (
            <div className="feepayment-field">
              <label>
                <input
                  type="checkbox"
                  checked={isRefund}
                  onChange={() => setIsRefund((r) => !r)}
                  style={{ marginRight: "8px" }}
                />
                Process Refund (Scholarship/Fee Return)
              </label>
            </div>
          )}
          {message && (
            <div
              className={`feepayment-message ${
                message.includes("✅") ? "success" : "error"
              }`}
            >
              {message}
            </div>
          )}
          {transactionId && message.includes("✅") && (
            <div className="feepayment-message success">
              Transaction ID: {transactionId}
            </div>
          )}
          <button
            className="feepayment-btn"
            type="submit"
            disabled={loading || showBillDesk}
          >
            {loading ? (isRefund ? "Processing..." : "Submitting...") : (isRefund ? "Process Refund" : "Pay Now")}
          </button>
        </form>
        {showBillDesk && (
          <div className="payment-options-modal">
            <h3>Redirecting to BillDesk Sandbox...</h3>
            <p>Please wait while we process your payment.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default FeePayment;